import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalculator,
  faEnvelope,
  faPhone,
  faClock,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import Container from '../ui/Container';
import businessInfo, {
  getEmailHref,
  getPhoneDisplay,
  getPhoneHref,
} from '../../utils/businessInfo';
import ROUTES from '../../utils/routes';
import {
  footerServiceLinks,
  footerCompanyLinks,
  footerLegalLinks,
} from '../../utils/navigation';

/**
 * Site footer.
 *
 * Contact values come from businessInfo so the bracketed placeholders can be
 * replaced in one place. `tel:` and `mailto:` links are only rendered when a
 * real value exists -- otherwise plain text is shown instead of a dead link.
 *
 * The scope-of-services statement appears once here rather than being
 * repeated across the marketing pages.
 */
const Footer = () => {
  const phoneHref = getPhoneHref();
  const emailHref = getEmailHref();
  const phoneDisplay = getPhoneDisplay();
  const year = new Date().getFullYear();

  return (
    <footer className="footer">
      <Container>
        <div className="footer__top">
          <div className="footer__brand">
            <Link to={ROUTES.home} className="site-brand site-brand--footer">
              <span className="site-brand__mark" aria-hidden="true">
                <FontAwesomeIcon icon={faCalculator} />
              </span>
              <span className="site-brand__text">
                <span className="site-brand__name">{businessInfo.name}</span>
                <span className="site-brand__tagline">{businessInfo.tagline}</span>
              </span>
            </Link>

            <p className="footer__blurb">
              Bookkeeping and accounting support for owner-operated businesses
              across California. We keep your records accurate, organized, and
              ready for your CPA.
            </p>

            <address className="footer__contact">
              <ul className="plain-list footer__contact-list">
                <li>
                  <FontAwesomeIcon icon={faPhone} aria-hidden="true" />
                  {phoneHref ? (
                    <a href={phoneHref}>{phoneDisplay}</a>
                  ) : (
                    <span>{phoneDisplay}</span>
                  )}
                </li>
                <li>
                  <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
                  {emailHref ? (
                    <a href={emailHref}>{businessInfo.email}</a>
                  ) : (
                    <span>{businessInfo.email}</span>
                  )}
                </li>
                <li>
                  <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                  <span>{businessInfo.serviceArea}</span>
                </li>
                <li>
                  <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                  <span>{businessInfo.businessHours}</span>
                </li>
              </ul>
            </address>
          </div>

          <nav className="footer__column" aria-labelledby="footer-services-heading">
            <h2 className="footer__heading" id="footer-services-heading">
              Services
            </h2>
            <ul className="footer__links">
              {footerServiceLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="footer__column" aria-labelledby="footer-company-heading">
            <h2 className="footer__heading" id="footer-company-heading">
              Company
            </h2>
            <ul className="footer__links">
              {footerCompanyLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="footer__column" aria-labelledby="footer-legal-heading">
            <h2 className="footer__heading" id="footer-legal-heading">
              Legal
            </h2>
            <ul className="footer__links">
              {footerLegalLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="footer__disclaimer">
          <h2 className="visually-hidden">Scope of services</h2>
          <p>
            {businessInfo.name} provides bookkeeping and accounting-support
            services. We are not a CPA firm and do not provide audits, attest
            services, or any service requiring CPA licensure. Nothing on this
            website constitutes legal, tax, or accounting advice, and no
            financial or tax outcome is promised. Please consult your CPA, tax
            professional, attorney, or another qualified professional for
            advice specific to your circumstances.
          </p>
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">
            &copy; {year} {businessInfo.legalName}. All rights reserved.
          </p>
          <ul className="footer__legal-links">
            {footerLegalLinks.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
